// Blueprint: forge/blueprint/bff/architecture/001-bff-auth.md
/*
 * Bridge between the out-of-React global 401 interceptor (unauthorized-interceptor.ts,
 * installed at standalone bootstrap) and the React/MobX auth surface (AuthStore). The
 * interceptor cannot reach into the DI container, so AuthStore registers a handler here
 * and the interceptor calls notifySessionExpired() when a session-expiry 401 is observed.
 */

// Registered by AuthStore; invoked once when the session is detected as expired.
let sessionExpiredHandler: (() => void) | null = null;

/*
 * Once-guard: a burst of simultaneous 401s must trigger the handler exactly once. Resets
 * naturally on the next full-page navigation (re-auth goes through /bff/login).
 */
let triggered = false;

export function setSessionExpiredHandler(handler: () => void): void {
    sessionExpiredHandler = handler;
}

export function notifySessionExpired(): void {
    if (triggered || !sessionExpiredHandler) {
        return;
    }
    triggered = true;
    sessionExpiredHandler();
}

/*
 * A 401 should return the user to the login screen only when it comes from a same-origin
 * authenticated request that is NOT part of the normal signed-out bootstrap. Excluded:
 *   - /api/me — the auth probe that always 401s while signed out (reacting would loop)
 *   - /bff/*  — the login/logout navigations themselves
 * Cross-origin 401s (CDN/telemetry) never trigger logout.
 */
export function shouldReactTo401(url: string): boolean {
    let parsed: URL;
    try {
        parsed = new URL(url, window.location.origin);
    } catch {
        return false;
    }

    if (parsed.origin !== window.location.origin) {
        return false;
    }

    const path = parsed.pathname;
    return path !== '/api/me' && !path.startsWith('/bff/');
}

// Test-only: re-arm the once-guard and clear the handler between tests.
export function resetSessionExpiryForTests(): void {
    sessionExpiredHandler = null;
    triggered = false;
}
